import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarClock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Panel, Label, StatusBadge, Empty } from "@/components/common";
import { useApi, errMsg, inr, fmtDate } from "@/lib/api";

const PlanRow = ({ p, showUser }) => (
  <div className="rounded-xl border border-[#1F293D] bg-[#0B0F17] p-4" data-testid={`plan-${p.id}`}>
    <div className="flex flex-wrap items-start justify-between gap-2">
      <div>
        <p className="font-display text-lg font-bold text-white">{p.product_name}</p>
        {showUser && <p className="text-xs text-slate-500">{p.name} · {p.email}</p>}
        <p className="text-xs text-slate-500">Started {fmtDate(p.created_at)}</p>
      </div>
      <StatusBadge status={p.status === "running" ? "active" : p.status === "completed" ? "delivered" : "rejected"} testId={`plan-status-${p.id}`} />
    </div>
    <div className="mt-3 flex justify-between font-mono text-xs text-slate-400">
      <span data-testid={`plan-days-${p.id}`}>Day {p.paid_days}/{p.days}</span>
      <span>{inr(p.daily_return)}/day · earned <span className="text-emerald-400">{inr(p.total_paid)}</span> of {inr(p.daily_return * p.days)}</span>
    </div>
    <Progress value={(p.paid_days / p.days) * 100} className="mt-2 h-1.5" />
    <p className="mt-2 text-[11px] uppercase tracking-widest text-slate-500">{p.status === "running" ? "Status: running" : `Status: ${p.status}`}</p>
  </div>
);

export const MyPlans = () => {
  const api = useApi();
  const [rows, setRows] = useState(null);
  useEffect(() => { api("get", "/me/investments").then(setRows).catch(() => setRows([])); }, [api]);
  if (!rows || rows.length === 0) return null;
  return (
    <Panel className="mt-6" data-testid="my-plans">
      <Label>My daily income plans</Label>
      <p className="mt-1 text-xs text-slate-500">Income is added to your wallet every day just after midnight (IST), starting the day after your order is confirmed.</p>
      <div className="mt-4 grid gap-4 md:grid-cols-2">{rows.map((p) => <PlanRow key={p.id} p={p} />)}</div>
    </Panel>
  );
};

export const AdminPlans = () => {
  const api = useApi();
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api("get", "/admin/investments").then(setRows), [api]);
  useEffect(() => { load(); }, [load]);
  const runNow = async () => {
    setBusy(true);
    try { const r = await api("post", "/admin/investments/run-today"); toast.success(r.plans_credited ? `Paid ${inr(r.amount)} to ${r.plans_credited} plan(s)` : "Nothing due today — all plans already paid"); load(); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const running = rows?.filter((r) => r.status === "running") || [];
  return (
    <Panel className="mt-6" data-testid="admin-plans">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Label>Daily income plans</Label>
          <p className="mt-1 text-xs text-slate-500">{running.length} running · paid automatically every day at 00:05 IST</p>
        </div>
        <Button variant="outline" size="sm" disabled={busy} onClick={runNow} data-testid="run-daily-returns-btn"><CalendarClock className="mr-1 h-4 w-4" />{busy ? "Paying…" : "Pay today's income now"}</Button>
      </div>
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        {!rows ? <p className="text-sm text-slate-500">Loading…</p> : rows.length === 0 ? <Empty testId="admin-plans-empty" text="No plans yet. Plans start when a member's order for a product with daily income is confirmed." /> : rows.map((p) => <PlanRow key={p.id} p={p} showUser />)}
      </div>
    </Panel>
  );
};
